import { useMemo, useState } from "react";
import type { DailyPoint } from "../api";

type Metric = "visitors" | "submits" | "duration";

type Props = {
  daily: DailyPoint[];
};

const TABS: { key: Metric; label: string }[] = [
  { key: "visitors", label: "Visitors" },
  { key: "submits", label: "Submits" },
  { key: "duration", label: "Time to Complete" },
];

const W = 720;
const H = 280;
const PAD_X = 36;
const PAD_TOP = 16;
const PAD_BOTTOM = 36;

function formatAxisDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  return `${dd}.${mm}.`;
}

function valueOf(point: DailyPoint, metric: Metric): number | null {
  if (metric === "visitors") return point.visitors;
  if (metric === "submits") return point.submits;
  // Duration is undefined on days with no completed sessions. Returning null
  // here lets the renderer draw a gap rather than a misleading dive to zero.
  return point.avg_duration_ms;
}

function formatTooltip(value: number, metric: Metric): string {
  if (metric === "duration") {
    const seconds = Math.round(value / 1000);
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  }
  return value.toLocaleString("en-US");
}

export function TimeSeriesChart({ daily }: Props) {
  const [metric, setMetric] = useState<Metric>("visitors");
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const series = useMemo(
    () => daily.map((d) => ({ point: d, value: valueOf(d, metric) })),
    [daily, metric],
  );

  const numericValues = series
    .map((s) => s.value)
    .filter((v): v is number => v !== null);
  const max = Math.max(1, ...numericValues);
  const innerW = W - PAD_X * 2;
  const innerH = H - PAD_TOP - PAD_BOTTOM;

  const xFor = (i: number): number => {
    if (series.length <= 1) return PAD_X + innerW / 2;
    return PAD_X + (i / (series.length - 1)) * innerW;
  };
  const yFor = (v: number): number =>
    PAD_TOP + innerH - (v / max) * innerH;

  // Split into contiguous segments of non-null points so the line draws gaps
  // on days with no data (avg_duration_ms is null when no sessions completed).
  const segments: { line: string; fill: string }[] = [];
  let current: { line: string; firstX: number; lastX: number } | null = null;
  series.forEach((s, i) => {
    if (s.value === null) {
      if (current) {
        segments.push({
          line: current.line,
          fill: `${current.line} L ${current.lastX} ${PAD_TOP + innerH} L ${current.firstX} ${PAD_TOP + innerH} Z`,
        });
        current = null;
      }
      return;
    }
    const x = xFor(i);
    const y = yFor(s.value);
    if (!current) {
      current = { line: `M ${x} ${y}`, firstX: x, lastX: x };
    } else {
      current.line += ` L ${x} ${y}`;
      current.lastX = x;
    }
  });
  if (current) {
    const c: { line: string; firstX: number; lastX: number } = current;
    segments.push({
      line: c.line,
      fill: `${c.line} L ${c.lastX} ${PAD_TOP + innerH} L ${c.firstX} ${PAD_TOP + innerH} Z`,
    });
  }

  const stepLabels = series.map((s) => formatAxisDate(s.point.date));
  const labelEvery = Math.max(1, Math.ceil(series.length / 7));

  return (
    <div className="rounded-2xl border border-mars-line bg-mars-charcoal/40 p-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setMetric(t.key)}
              className={
                metric === t.key
                  ? "mono rounded-md bg-mars-orange px-3 py-1.5 text-[0.65rem] uppercase tracking-[0.16em] text-mars-black"
                  : "mono rounded-md px-3 py-1.5 text-[0.65rem] uppercase tracking-[0.16em] text-mars-white/60 transition-colors hover:bg-mars-charcoal hover:text-mars-white"
              }
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div className="relative w-full">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          className="h-72 w-full"
          role="img"
          aria-label={`${metric} over time`}
          onMouseLeave={() => setHoverIdx(null)}
        >
          <defs>
            <linearGradient id="ts-fill" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#ff5600" stopOpacity="0.32" />
              <stop offset="100%" stopColor="#ff5600" stopOpacity="0" />
            </linearGradient>
          </defs>
          {[0, 0.25, 0.5, 0.75, 1].map((t) => (
            <line
              key={t}
              x1={PAD_X}
              x2={W - PAD_X}
              y1={PAD_TOP + innerH * t}
              y2={PAD_TOP + innerH * t}
              stroke="#2a2a2a"
              strokeWidth={1}
            />
          ))}
          {segments.map((seg, i) => (
            <g key={`seg-${i}`}>
              <path d={seg.fill} fill="url(#ts-fill)" />
              <path
                d={seg.line}
                fill="none"
                stroke="#ff5600"
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            </g>
          ))}
          {series.map((s, i) =>
            i % labelEvery === 0 || i === series.length - 1 ? (
              <text
                key={`x-${i}`}
                x={xFor(i)}
                y={H - 12}
                textAnchor="middle"
                className="mono"
                fill="rgba(255,255,255,0.45)"
                fontSize={10}
                style={{ letterSpacing: "0.08em" }}
              >
                {stepLabels[i]}
              </text>
            ) : null,
          )}
          {[0, max / 2, max].map((v, idx) => (
            <text
              key={`y-${idx}`}
              x={PAD_X - 6}
              y={yFor(v) + 3}
              textAnchor="end"
              className="mono"
              fill="rgba(255,255,255,0.35)"
              fontSize={10}
            >
              {metric === "duration"
                ? formatTooltip(v, "duration")
                : Math.round(v).toLocaleString("en-US")}
            </text>
          ))}
          {series.map((s, i) => (
            <rect
              key={`hit-${i}`}
              x={xFor(i) - innerW / Math.max(1, series.length) / 2}
              y={PAD_TOP}
              width={Math.max(8, innerW / Math.max(1, series.length))}
              height={innerH}
              fill="transparent"
              onMouseEnter={() => setHoverIdx(i)}
            />
          ))}
          {hoverIdx !== null && series[hoverIdx] && (
            <>
              <line
                x1={xFor(hoverIdx)}
                x2={xFor(hoverIdx)}
                y1={PAD_TOP}
                y2={PAD_TOP + innerH}
                stroke="rgba(255,255,255,0.25)"
                strokeWidth={1}
              />
              {series[hoverIdx]!.value !== null && (
                <circle
                  cx={xFor(hoverIdx)}
                  cy={yFor(series[hoverIdx]!.value as number)}
                  r={4}
                  fill="#ff5600"
                  stroke="#000"
                  strokeWidth={2}
                />
              )}
            </>
          )}
        </svg>
        {hoverIdx !== null && series[hoverIdx] && (
          <div
            className="pointer-events-none absolute -translate-x-1/2 -translate-y-full rounded-md border border-mars-line bg-mars-night px-2.5 py-1.5 shadow-lg"
            style={{
              left: `${(xFor(hoverIdx) / W) * 100}%`,
              top: `${
                (yFor(series[hoverIdx]!.value ?? 0) / H) * 100
              }%`,
            }}
          >
            <div className="eyebrow">{formatAxisDate(series[hoverIdx]!.point.date)}</div>
            <div className="mono mt-0.5 text-sm text-mars-white tabular-nums">
              {series[hoverIdx]!.value === null
                ? "No data"
                : formatTooltip(series[hoverIdx]!.value as number, metric)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
